import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, ArrowLeft, Trash2, Eye, Download, Share2, Link as LinkIcon, FileArchive, Loader2, HelpCircle, Wand2 } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import FacebookImportGuide from '../components/FacebookImportGuide';

const AlbumView = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUrlImport, setShowUrlImport] = useState(false);
  const [showFacebookGuide, setShowFacebookGuide] = useState(false);
  const [showSmartSelection, setShowSmartSelection] = useState(false);
  const [showLibrarySelection, setShowLibrarySelection] = useState(false);
  const [showAlbumSettings, setShowAlbumSettings] = useState(false);
  const [albumSettings, setAlbumSettings] = useState({
    title: '',
    description: '',
    year: new Date().getFullYear(),
    ai_instructions: ''
  });
  const [libraryPhotos, setLibraryPhotos] = useState([]);
  const [filteredLibraryPhotos, setFilteredLibraryPhotos] = useState([]);
  const [librarySearchQuery, setLibrarySearchQuery] = useState('');
  const [selectedLibraryPhotos, setSelectedLibraryPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [smartCriteria, setSmartCriteria] = useState({
    max_photos: 50,
    date_from: '',
    date_to: '',
    keywords: '',
    min_significance: 5,
    exclude_keywords: '',
    ai_instructions: ''
  });
  const [shoeboxes, setShoeboxes] = useState([]);
  const [selectedSource, setSelectedSource] = useState('entire_library'); // 'entire_library' or 'shoebox'
  const [selectedShoeboxId, setSelectedShoeboxId] = useState('');

  useEffect(() => {
    fetchAlbum();
    fetchPhotos();
    fetchShoeboxes();
  }, [albumId]);

  useEffect(() => {
    if (album) {
      setAlbumSettings({
        title: album.title || '',
        description: album.description || '',
        year: album.year || new Date().getFullYear(),
        ai_instructions: album.ai_instructions || ''
      });
      // Pre-fill AI instructions in smart criteria from album
      setSmartCriteria(prev => ({
        ...prev,
        ai_instructions: album.ai_instructions || ''
      }));
    }
  }, [album]);

  const fetchAlbum = async () => {
    try {
      const response = await axios.get(`${API}/albums/${albumId}`);
      setAlbum(response.data);
    } catch (error) {
      toast.error('Failed to load album');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`${API}/albums/${albumId}/photos`);
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load photos');
    }
  };

  const fetchShoeboxes = async () => {
    try {
      const response = await axios.get(`${API}/shoeboxes`);
      setShoeboxes(response.data);
    } catch (error) {
      console.error('Failed to load shoeboxes');
    }
  };

  const getErrorMessage = (error) => {
    if (typeof error.response?.data?.detail === 'string') {
      return error.response.data.detail;
    }
    if (Array.isArray(error.response?.data?.detail)) {
      return error.response.data.detail.map(err => err.msg).join(', ');
    }
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    return error.message || 'An error occurred';
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(`Uploading ${files.length} photo(s)...`);
    
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    try {
      const response = await axios.post(`${API}/albums/${albumId}/photos`, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setUploadStatus(`Uploading ${files.length} photo(s)... ${percentCompleted}%`);
        }
      });
      setUploadStatus('Processing with AI...');
      toast.success(`Successfully uploaded ${response.data.uploaded_count} photos`);
      fetchPhotos();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to upload photos');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      e.target.value = '';
    }
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading ZIP file...');
    
    const formData = new FormData();
    formData.append('zip_file', file);

    try {
      const response = await axios.post(`${API}/albums/${albumId}/upload-zip`, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          setUploadStatus(`Uploading ZIP... ${percentCompleted}%`);
        }
      });
      setUploadStatus('Extracting and processing with AI...');
      toast.success(`Successfully uploaded ${response.data.uploaded_count} photos`);
      fetchPhotos();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to upload ZIP');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
      e.target.value = '';
    }
  };

  const handleAISmartSelection = async () => {
    // Validate selection
    if (selectedSource === 'shoebox' && !selectedShoeboxId) {
      toast.error('Please select a shoebox');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('AI analyzing photos...');
    
    // Prepare criteria
    const criteria = {
      max_photos: parseInt(smartCriteria.max_photos) || 50,
      min_significance: parseInt(smartCriteria.min_significance) || 5,
      date_from: smartCriteria.date_from || null,
      date_to: smartCriteria.date_to || null,
      keywords: smartCriteria.keywords ? smartCriteria.keywords.split(',').map(k => k.trim()).filter(k => k) : null,
      exclude_keywords: smartCriteria.exclude_keywords ? smartCriteria.exclude_keywords.split(',').map(k => k.trim()).filter(k => k) : null,
      ai_instructions: smartCriteria.ai_instructions || null,
      source: selectedSource,
      shoebox_id: selectedSource === 'shoebox' ? selectedShoeboxId : null
    };

    try {
      const response = await axios.post(`${API}/albums/${albumId}/ai-smart-select`, criteria);
      toast.success(`AI selected ${response.data.selected_count} of ${response.data.total_analyzed} photos based on your criteria`);
      fetchPhotos();
      fetchAlbum();
      setShowSmartSelection(false);
      
      // Reset form
      setSelectedSource('entire_library');
      setSelectedShoeboxId('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to perform AI selection');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const handleUrlImport = async (e) => {
    e.preventDefault();
    if (!imageUrl.trim()) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image_url', imageUrl);

    try {
      const response = await axios.post(`${API}/albums/${albumId}/import-url`, formData);
      toast.success('Image imported successfully!');
      setImageUrl('');
      setShowUrlImport(false);
      fetchPhotos();
      fetchAlbum();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed. Please check the URL.');
    } finally {
      setUploading(false);
    }
  };

  const fetchLibraryPhotos = async () => {
    try {
      const response = await axios.get(`${API}/library`);
      setLibraryPhotos(response.data);
      setFilteredLibraryPhotos(response.data);
      setShowLibrarySelection(true);
    } catch (error) {
      toast.error('Failed to load library photos');
    }
  };

  const handleLibrarySearch = (query) => {
    setLibrarySearchQuery(query);
    if (!query.trim()) {
      setFilteredLibraryPhotos(libraryPhotos);
    } else {
      const filtered = libraryPhotos.filter(photo => 
        photo.caption?.toLowerCase().includes(query.toLowerCase()) ||
        photo.filename?.toLowerCase().includes(query.toLowerCase()) ||
        photo.location?.toLowerCase().includes(query.toLowerCase()) ||
        photo.original_date?.includes(query)
      );
      setFilteredLibraryPhotos(filtered);
    }
  };

  const toggleLibraryPhotoSelection = (photoId) => {
    setSelectedLibraryPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleAddLibraryPhotos = async () => {
    if (selectedLibraryPhotos.length === 0) {
      toast.error('Please select photos first');
      return;
    }

    try {
      const response = await axios.post(`${API}/albums/${albumId}/add-photos`, selectedLibraryPhotos);
      toast.success(response.data.message);
      setSelectedLibraryPhotos([]);
      setShowLibrarySelection(false);
      fetchPhotos();
      fetchAlbum();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add photos to album');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      await axios.delete(`${API}/photos/${photoId}`);
      toast.success('Photo deleted');
      fetchPhotos();
      fetchAlbum();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleUpdatePhoto = async (photoId, updates) => {
    try {
      await axios.put(`${API}/photos/${photoId}`, updates);
      toast.success('Photo updated');
      setShowEditDialog(false);
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleShareAlbum = async () => {
    try {
      const response = await axios.put(`${API}/albums/${albumId}`, { is_public: true });
      const updatedAlbum = response.data;
      
      // Use the share_token from the response
      const shareToken = updatedAlbum.share_token;
      if (!shareToken) {
        toast.error('Failed to generate share token');
        return;
      }
      
      const shareUrl = `${window.location.origin}/share/${shareToken}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
      
      // Refresh album data
      await fetchAlbum();
    } catch (error) {
      toast.error('Failed to generate share link');
    }
  };

  const handleDeleteAlbum = async () => {
    if (!window.confirm('Are you sure you want to delete this album? Photos will remain in your library.')) {
      return;
    }

    try {
      await axios.delete(`${API}/albums/${albumId}`);
      toast.success('Album deleted. Photos are still in your library.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete album');
    }
  };

  const handleUpdateAlbumSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/albums/${albumId}`, albumSettings);
      toast.success('Album settings updated!');
      setShowAlbumSettings(false);
      fetchAlbum();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update album settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      <header className="bg-white/60 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button data-testid="back-btn" variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{album?.title}</h1>
                <p className="text-sm text-gray-600">{photos.length} photos</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="view-flipbook-btn"
                onClick={() => navigate(`/album/${albumId}/flipbook`)}
                variant="outline"
                disabled={photos.length === 0}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Flipbook
              </Button>
              <Button data-testid="share-album-btn" onClick={handleShareAlbum} variant="outline">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button 
                data-testid="album-settings-btn" 
                onClick={() => setShowAlbumSettings(true)} 
                variant="outline"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <Button 
                data-testid="delete-album-btn" 
                onClick={handleDeleteAlbum} 
                variant="outline"
                className="text-red-600 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Album
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Progress Bar */}
        {uploading && (
          <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{uploadStatus}</span>
              <span className="text-sm font-semibold text-blue-600">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
        
        <div className="mb-6 flex gap-3">
          <Button 
            data-testid="upload-photos-btn" 
            disabled={uploading} 
            className="bg-gradient-to-r from-amber-600 to-rose-500 text-white"
            onClick={() => document.getElementById('file-upload').click()}
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Upload Photos'}
          </Button>
          <input
            id="file-upload"
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />

          <Button 
            data-testid="upload-zip-btn" 
            variant="outline" 
            disabled={uploading}
            onClick={() => document.getElementById('zip-upload').click()}
          >
            <FileArchive className="w-4 h-4 mr-2" />
            Upload Zip
          </Button>
          <input
            id="zip-upload"
            type="file"
            accept=".zip"
            onChange={handleZipUpload}
            className="hidden"
            disabled={uploading}
          />

          <Button 
            data-testid="smart-zip-btn" 
            className="bg-gradient-to-r from-purple-600 to-blue-500 text-white"
            disabled={uploading}
            onClick={() => setShowSmartSelection(true)}
          >
            <FileArchive className="w-4 h-4 mr-2" />
            AI Smart Selection
          </Button>

          <Button 
            className="bg-gradient-to-r from-green-600 to-teal-500 text-white"
            disabled={uploading}
            onClick={fetchLibraryPhotos}
          >
            <FileArchive className="w-4 h-4 mr-2" />
            Select from Library
          </Button>

          <Button
            data-testid="import-url-btn"
            onClick={() => setShowUrlImport(true)}
            variant="outline"
            disabled={uploading}
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            Import from URL
          </Button>

          <Button
            data-testid="facebook-guide-btn"
            onClick={() => setShowFacebookGuide(true)}
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Facebook Import Guide
          </Button>
        </div>

        {/* Facebook Import Guide */}
        <FacebookImportGuide open={showFacebookGuide} onOpenChange={setShowFacebookGuide} />

        <div className="photo-grid">
          {photos.map(photo => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onDelete={handleDeletePhoto}
              onEdit={(photo) => {
                setSelectedPhoto(photo);
                setShowEditDialog(true);
              }}
            />
          ))}
        </div>
      </main>

      {/* URL Import Dialog */}
      <Dialog open={showUrlImport} onOpenChange={setShowUrlImport}>
        <DialogContent data-testid="url-import-dialog">
          <DialogHeader>
            <DialogTitle>Import from URL</DialogTitle>
            <DialogDescription>
              Import images from Google Photos, iCloud, or any direct image URL
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUrlImport} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="image-url">Image URL</Label>
              <Input
                data-testid="image-url-input"
                id="image-url"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                required
              />
              <p className="text-xs text-gray-600 mb-2">
                Paste a direct link to an image from Google Photos, iCloud, Facebook, or any image hosting service
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowUrlImport(false);
                  setShowFacebookGuide(true);
                }}
                className="text-xs text-blue-600 hover:underline"
              >
                Need help getting Facebook photo URLs? Click here
              </button>
            </div>
            <Button
              data-testid="import-url-submit-btn"
              type="submit"
              disabled={uploading}
              className="w-full bg-gradient-to-r from-amber-600 to-rose-500 text-white"
            >
              {uploading ? 'Importing...' : 'Import Image'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Photo Dialog */}
      {selectedPhoto && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent data-testid="edit-photo-dialog">
            <DialogHeader>
              <DialogTitle>Edit Photo</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleUpdatePhoto(selectedPhoto.id, {
                  caption: formData.get('caption'),
                  location: formData.get('location'),
                  original_date: formData.get('original_date')
                });
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="caption">Caption</Label>
                <Textarea
                  data-testid="photo-caption-input"
                  id="caption"
                  name="caption"
                  defaultValue={selectedPhoto.caption || ''}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  data-testid="photo-location-input"
                  id="location"
                  name="location"
                  defaultValue={selectedPhoto.location || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="original_date">Date</Label>
                <Input
                  data-testid="photo-date-input"
                  id="original_date"
                  name="original_date"
                  defaultValue={selectedPhoto.original_date || ''}
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-amber-600 to-rose-500 text-white">
                Save Changes
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Smart Selection Dialog */}
      <Dialog open={showSmartSelection} onOpenChange={setShowSmartSelection}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5" />
              AI Smart Selection
            </DialogTitle>
            <DialogDescription>
              Let AI automatically select the best photos from your library or a specific shoebox
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* Source Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">1. Select Photo Source</Label>
              <div className="space-y-3">
                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedSource === 'entire_library' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedSource('entire_library')}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={selectedSource === 'entire_library'}
                      onChange={() => setSelectedSource('entire_library')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Entire Library</p>
                      <p className="text-sm text-gray-600">AI will analyze all photos in your library</p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedSource === 'shoebox' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedSource('shoebox')}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      checked={selectedSource === 'shoebox'}
                      onChange={() => setSelectedSource('shoebox')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Specific Shoebox</p>
                      <p className="text-sm text-gray-600 mb-3">AI will analyze photos from a selected upload batch</p>
                      {selectedSource === 'shoebox' && (
                        <select
                          value={selectedShoeboxId}
                          onChange={(e) => setSelectedShoeboxId(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">-- Select a Shoebox --</option>
                          {shoeboxes.map((shoebox) => (
                            <option key={shoebox.id} value={shoebox.id}>
                              {shoebox.name} ({shoebox.photo_count} photos)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Criteria */}
            <div className="space-y-4 border-t pt-4">
              <Label className="text-base font-semibold">2. AI Selection Criteria</Label>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max-photos">Maximum Photos</Label>
                  <Input
                    id="max-photos"
                    type="number"
                    placeholder="50"
                    value={smartCriteria.max_photos}
                    onChange={(e) => setSmartCriteria({...smartCriteria, max_photos: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">How many photos to select at most</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="min-significance">Minimum Quality (1-10)</Label>
                  <Input
                    id="min-significance"
                    type="number"
                    min="1"
                    max="10"
                    placeholder="5"
                    value={smartCriteria.min_significance}
                    onChange={(e) => setSmartCriteria({...smartCriteria, min_significance: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">Only select photos with this score or higher</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date-from">Date From</Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={smartCriteria.date_from}
                    onChange={(e) => setSmartCriteria({...smartCriteria, date_from: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date-to">Date To</Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={smartCriteria.date_to}
                    onChange={(e) => setSmartCriteria({...smartCriteria, date_to: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="keywords">Include Keywords (comma-separated)</Label>
                <Input
                  id="keywords"
                  placeholder="beach, sunset, family, vacation"
                  value={smartCriteria.keywords}
                  onChange={(e) => setSmartCriteria({...smartCriteria, keywords: e.target.value})}
                />
                <p className="text-xs text-gray-500">Only select photos that match at least one keyword</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="exclude-keywords">Exclude Keywords (comma-separated)</Label>
                <Input
                  id="exclude-keywords"
                  placeholder="selfie, food, screenshot"
                  value={smartCriteria.exclude_keywords}
                  onChange={(e) => setSmartCriteria({...smartCriteria, exclude_keywords: e.target.value})}
                />
                <p className="text-xs text-gray-500">Skip photos that match these keywords</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai-instructions" className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4" />
                  AI Instructions for This Album
                </Label>
                <Textarea
                  id="ai-instructions"
                  rows={5}
                  placeholder="Enter custom instructions or use the default..."
                  value={smartCriteria.ai_instructions}
                  onChange={(e) => setSmartCriteria({...smartCriteria, ai_instructions: e.target.value})}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  These instructions guide AI in selecting photos. They're taken from your album settings and can be edited here.
                </p>
              </div>
            </div>

            {/* Action Button */}
            <div className="border-t pt-4">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>How it works:</strong> AI will analyze photos from your selected source and automatically 
                  pick the best ones based on your criteria and instructions.
                </p>
                <p className="text-xs text-gray-600">
                  ✨ Photos will be ranked by significance and relevance to your criteria
                </p>
              </div>
              <Button
                onClick={handleAISmartSelection}
                disabled={uploading || (selectedSource === 'shoebox' && !selectedShoeboxId)}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl"
                size="lg"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    AI Analyzing Photos...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    Run AI Smart Selection
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Library Photo Selection Dialog */}
      <Dialog open={showLibrarySelection} onOpenChange={setShowLibrarySelection}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Photos from Library</DialogTitle>
            <DialogDescription>
              Choose photos from your library to add to this album ({libraryPhotos.length} photos total)
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 flex flex-col overflow-hidden">
            {libraryPhotos.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">Your library is empty</p>
                <Button
                  onClick={() => {
                    setShowLibrarySelection(false);
                    navigate('/library');
                  }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                >
                  Go to Library
                </Button>
              </div>
            ) : (
              <>
                {/* Search Bar */}
                <div className="mb-4">
                  <Input
                    placeholder="Search by caption, filename, location, or date..."
                    value={librarySearchQuery}
                    onChange={(e) => handleLibrarySearch(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Showing {filteredLibraryPhotos.length} of {libraryPhotos.length} photos
                  </p>
                </div>
                
                {/* Photo Grid - Scrollable */}
                <div className="flex-1 overflow-y-auto pr-2">
                  <div className="grid grid-cols-5 gap-3">
                    {filteredLibraryPhotos.map((photo) => (
                      <LibraryPhotoThumb
                        key={photo.id}
                        photo={photo}
                        isSelected={selectedLibraryPhotos.includes(photo.id)}
                        onToggle={() => toggleLibraryPhotoSelection(photo.id)}
                      />
                    ))}
                  </div>
                </div>
                {/* Actions Footer */}
                <div className="flex justify-between items-center border-t pt-4 mt-4">
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-gray-700">
                      {selectedLibraryPhotos.length} selected
                    </p>
                    {selectedLibraryPhotos.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedLibraryPhotos([])}
                      >
                        Clear selection
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedLibraryPhotos([]);
                        setLibrarySearchQuery('');
                        setShowLibrarySelection(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAddLibraryPhotos}
                      disabled={selectedLibraryPhotos.length === 0}
                      className="bg-gradient-to-r from-green-600 to-teal-500 text-white"
                    >
                      Add {selectedLibraryPhotos.length > 0 && `${selectedLibraryPhotos.length} `}Photos
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Album Settings Dialog */}
      <Dialog open={showAlbumSettings} onOpenChange={setShowAlbumSettings}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Album Settings</DialogTitle>
            <DialogDescription>Update album details and AI instructions</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateAlbumSettings} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="settings-title">Album Title</Label>
              <Input
                id="settings-title"
                value={albumSettings.title}
                onChange={(e) => setAlbumSettings({ ...albumSettings, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-description">Description</Label>
              <Textarea
                id="settings-description"
                value={albumSettings.description}
                onChange={(e) => setAlbumSettings({ ...albumSettings, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-year">Year</Label>
              <Input
                id="settings-year"
                type="number"
                value={albumSettings.year}
                onChange={(e) => setAlbumSettings({ ...albumSettings, year: parseInt(e.target.value) })}
                min="1900"
                max="2100"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-ai-instructions" className="flex items-center gap-2">
                <Wand2 className="w-4 h-4" />
                AI Instructions
              </Label>
              <Textarea
                id="settings-ai-instructions"
                value={albumSettings.ai_instructions}
                onChange={(e) => setAlbumSettings({ ...albumSettings, ai_instructions: e.target.value })}
                rows={6}
                className="text-sm font-mono"
              />
              <p className="text-xs text-gray-500">
                These instructions guide AI when analyzing and curating photos for this album.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowAlbumSettings(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-amber-600 to-rose-500 text-white">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LibraryPhotoThumb = ({ photo, isSelected, onToggle }) => {
  const [error, setError] = useState(false);
  
  // Direct image URL - no fetch needed, browser loads it directly
  const imageUrl = `${API}/photos/${photo.id}/image?thumbnail=true`;

  return (
    <div
      className={`relative cursor-pointer rounded-lg overflow-hidden border-4 transition-all ${
        isSelected
          ? 'border-green-500 shadow-lg'
          : 'border-transparent hover:border-gray-300'
      }`}
      onClick={onToggle}
    >
      {error ? (
        <div className="w-full h-32 bg-red-100 flex items-center justify-center">
          <span className="text-red-600 text-xs">Failed to load</span>
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={photo.filename}
          className="w-full h-32 object-cover bg-gray-200"
          loading="lazy"
          onError={() => setError(true)}
        />
      )}
      {isSelected && (
        <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
          ✓
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
        <p className="text-white text-xs truncate">{photo.caption || photo.filename}</p>
      </div>
    </div>
  );
};

const PhotoCard = ({ photo, onDelete, onEdit }) => {
  const [error, setError] = useState(false);
  
  // Use direct thumbnail URL for fast loading
  const imageUrl = `${API}/photos/${photo.id}/image?thumbnail=true`;

  return (
    <div data-testid={`photo-card-${photo.id}`} className="bg-white rounded-lg overflow-hidden border border-gray-200 hover-lift group">
      <div className="relative aspect-square">
        {error ? (
          <div className="w-full h-full bg-red-100 flex items-center justify-center">
            <span className="text-red-600 text-xs">Failed to load</span>
          </div>
        ) : (
          <img 
            src={imageUrl} 
            alt={photo.caption || photo.filename} 
            className="w-full h-full object-cover bg-gray-200"
            loading="lazy"
            onError={() => setError(true)}
          />
        )}
        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            data-testid={`delete-photo-${photo.id}`}
            size="sm"
            variant="destructive"
            onClick={() => onDelete(photo.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="p-4">
        <p className="text-sm text-gray-900 font-medium mb-1 line-clamp-2">{photo.caption || photo.filename}</p>
        {photo.location && <p className="text-xs text-gray-600 mb-1">{photo.location}</p>}
        {photo.original_date && <p className="text-xs text-gray-500">{photo.original_date}</p>}
        <Button
          data-testid={`edit-photo-${photo.id}`}
          variant="ghost"
          size="sm"
          onClick={() => onEdit(photo)}
          className="mt-2 w-full"
        >
          Edit Details
        </Button>
      </div>
    </div>
  );
};

export default AlbumView;
